import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Search, CalendarDays, Users, MapPin } from 'lucide-react';
import { toast } from 'sonner';
import { GeoFilters } from '@/components/GeoFilters';

const DAYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const DAY_SHORT = { monday: 'Mon', tuesday: 'Tue', wednesday: 'Wed', thursday: 'Thu', friday: 'Fri', saturday: 'Sat', sunday: 'Sun' };
const DAY_LABEL = { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' };

export default function BeatPlanPage() {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState({ grid: [], list: [], total_fes: 0, total_beats: 0, filters: { fes: [], zones: [] } });
  const [day, setDay] = useState('');
  const [zone, setZone] = useState('');
  const [subzone, setSubzone] = useState('');
  const [state, setState] = useState('');
  const [city, setCity] = useState('');
  const [fe, setFe] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    const params = {};
    if (day) params.day = day;
    if (zone) params.zone = zone;
    if (subzone) params.subzone = subzone;
    if (state) params.state = state;
    if (city) params.city = city;
    if (fe) params.fe = fe;
    if (search) params.search = search;
    api.get('/beat-plans/view', { params }).then(r => setData(r.data)).catch(() => toast.error('Failed to load beat plans')).finally(() => setLoading(false));
  }, [api, day, zone, subzone, state, city, fe, search]);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const t = setTimeout(() => setSearch(searchInput), 300);
    return () => clearTimeout(t);
  }, [searchInput]);

  const today = DAYS[new Date().getDay() === 0 ? 6 : new Date().getDay() - 1];

  return (
    <div className="space-y-5" data-testid="beat-plan-page">
      <div>
        <h1 className="text-2xl sm:text-3xl font-heading font-bold tracking-tight">Beat Plans</h1>
        <p className="text-sm text-muted-foreground mt-1">Weekly beat schedule — who visits where on which day</p>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 items-center">
        <Select value={day || 'all'} onValueChange={v => setDay(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-40" data-testid="filter-day"><SelectValue placeholder="All Days" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Days</SelectItem>
            {DAYS.map(d => <SelectItem key={d} value={d}>{DAY_LABEL[d]}{d === today ? ' (Today)' : ''}</SelectItem>)}
          </SelectContent>
        </Select>
        <GeoFilters api={api} zone={zone} setZone={setZone} subzone={subzone} setSubzone={setSubzone}
          state={state} setState={setState} city={city} setCity={setCity} />
        <Select value={fe || 'all'} onValueChange={v => setFe(v === 'all' ? '' : v)}>
          <SelectTrigger className="w-56" data-testid="filter-fe"><SelectValue placeholder="All FEs" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All FEs</SelectItem>
            {data.filters.fes.map(f => (
              <SelectItem key={f.id} value={f.id}>
                <span>{f.name} <span className="text-[10px] text-muted-foreground ml-1">({f.id})</span></span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Search beat, FE or user ID..." value={searchInput} onChange={e => setSearchInput(e.target.value)} className="pl-8 w-60" data-testid="beat-search" />
        </div>
        <span className="text-xs text-muted-foreground ml-auto">{data.total_fes} FEs, {data.total_beats} beats</span>
      </div>

      {loading ? (
        <div className="text-center py-16 text-muted-foreground text-sm">Loading beat plans...</div>
      ) : data.grid.length === 0 ? (
        <div className="bg-card border border-border rounded-md p-12 text-center">
          <CalendarDays size={36} strokeWidth={1} className="mx-auto text-muted-foreground mb-3" />
          <h3 className="font-heading font-semibold text-lg mb-2">No Beat Plans</h3>
          <p className="text-sm text-muted-foreground">Upload Beat Plan CSV from Configuration to see the weekly schedule.</p>
        </div>
      ) : !day ? (
        /* Weekly Grid View */
        <div className="bg-card border border-border rounded-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-muted/50 border-b border-border">
                  <th className="text-left px-3 py-2.5 font-heading font-semibold text-sm sticky left-0 bg-muted/50 min-w-[160px]">Field Executive</th>
                  {DAYS.map(d => (
                    <th key={d} className={`text-center px-2 py-2.5 font-heading font-medium min-w-[110px] ${d === today ? 'bg-primary/5 text-primary' : ''}`}>
                      {DAY_SHORT[d]}
                      {d === today && <span className="ml-1 text-[9px] bg-primary text-primary-foreground px-1.5 py-0.5 rounded-full font-medium align-middle">Today</span>}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.grid.map((fe, fi) => (
                  <tr key={fe.fe_username} className="border-b border-border/50 hover:bg-muted/20 transition-colors">
                    <td className="px-3 py-2 sticky left-0 bg-card">
                      <div className="font-medium text-sm">{fe.fe_name}</div>
                      <div className="text-[10px] text-muted-foreground">{fe.fe_username} · {fe.zone}</div>
                    </td>
                    {DAYS.map(d => {
                      const beats = fe.days[d] || [];
                      return (
                        <td key={d} className={`px-2 py-2 text-center align-top ${d === today ? 'bg-primary/5' : ''}`}>
                          {beats.length === 0 ? (
                            <span className="text-muted-foreground/30">-</span>
                          ) : beats.map((b, bi) => (
                            <div key={bi} className={`${bi > 0 ? 'mt-1.5 pt-1.5 border-t border-border/30' : ''}`}>
                              <div className="font-medium text-[11px] leading-tight">{b.beat}</div>
                              <div className="text-[9px] text-muted-foreground">{b.outlets} outlets</div>
                            </div>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        /* Day Detail View */
        <div className="space-y-3">
          <h2 className="font-heading font-semibold text-sm text-primary">{DAY_LABEL[day]} — {data.total_fes} FEs, {data.total_beats} beats</h2>
          {data.grid.map(fe => {
            const beats = fe.days[day] || [];
            if (beats.length === 0) return null;
            return (
              <div key={fe.fe_username} className="bg-card border border-border rounded-md p-4">
                <div className="flex items-center gap-2 mb-2">
                  <Users size={14} className="text-primary" />
                  <span className="font-heading font-semibold text-sm">{fe.fe_name}</span>
                  <span className="text-xs text-muted-foreground">({fe.fe_username})</span>
                  <span className="text-xs text-muted-foreground ml-auto">Zone: {fe.zone} · Reports to: {fe.reporting_to}</span>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
                  {beats.map((b, bi) => (
                    <div key={bi} className="flex items-center gap-2 bg-muted/30 rounded px-3 py-2">
                      <MapPin size={13} className="text-muted-foreground flex-shrink-0" />
                      <div>
                        <div className="font-medium text-xs">{b.beat}</div>
                        <div className="text-[10px] text-muted-foreground">{b.outlets} outlets{b.distributor ? ` · ${b.distributor}` : ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
